import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShoppingBag } from "lucide-react";
import PurchaseModal from "@/components/PurchaseModal";
import { PRODUCTS, formatPrice } from "@/data/products";

// Pack first, then 3 more products — all as equal cards on the home.
const HOME_PRODUCTS = [PRODUCTS[0], PRODUCTS[1], PRODUCTS[2], PRODUCTS[4]];

export default function FeaturedProducts() {
  const [selected, setSelected] = useState(null);

  return (
    <section
      id="tienda"
      data-testid="featured-products-section"
      className="relative py-32 md:py-40 overflow-hidden scroll-mt-24"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[#FFB800]" />
              06 — Tienda
            </p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-tight leading-[0.95]">
              Los socios<br />
              <span className="italic font-light text-white/70">visten el escudo.</span>
            </h2>
          </div>
          <p className="text-white/55 max-w-sm text-[15px]">
            Camisetas, shorts y packs oficiales. Personalizables con nombre
            y número. Producción cuidada y materiales premium.
          </p>
        </div>

        {/* Equal cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOME_PRODUCTS.map((p) => (
            <ProductCard key={p.slug} product={p} onBuy={() => setSelected(p)} />
          ))}
        </div>

        {/* General CTA */}
        <div className="mt-14 flex justify-center">
          <Link
            to="/tienda"
            data-testid="featured-products-cta-all"
            className="btn-primary"
          >
            Ver tienda
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>

      <PurchaseModal
        product={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </section>
  );
}

function ProductCard({ product, onBuy }) {
  const isPack = product.category === "Pack";
  const discount = product.oldPrice
    ? Math.round((1 - product.price / product.oldPrice) * 100)
    : 0;

  return (
    <article
      data-testid={`home-product-card-${product.slug}`}
      className={`group relative rounded-[2rem] overflow-hidden bg-[#0A1121] border transition-all duration-500 flex flex-col ${
        isPack
          ? "border-[#FFB800]/40 hover:border-[#FFB800]"
          : "border-white/8 hover:border-[#FFB800]/40"
      }`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#0B1D3A]">
        <img
          src={product.images[0]}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        {isPack && (
          <span
            className="absolute top-4 left-4 rounded-full bg-[#FFB800] text-[#030712] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] font-bold"
          >
            ★ Más vendido
          </span>
        )}
        {!isPack && discount > 0 && (
          <span className="absolute top-4 left-4 rounded-full bg-[#FFB800] text-[#030712] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] font-bold">
            -{discount}%
          </span>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] mb-2">
          {product.category}
        </span>
        <h3 className="font-display text-xl md:text-[22px] font-extrabold uppercase tracking-tight leading-tight">
          {product.name}
        </h3>
        <p className="text-white/55 text-[13px] mt-2 leading-relaxed line-clamp-2">
          {product.short}
        </p>

        <div className="mt-auto pt-6 flex items-end justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-2xl font-extrabold text-white">
              {formatPrice(product.price)}
            </span>
            {product.oldPrice && (
              <span className="text-xs text-white/35 line-through">
                {formatPrice(product.oldPrice)}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onBuy}
            data-testid={`home-buy-${product.slug}`}
            className="rounded-full bg-[#FFB800] hover:bg-white text-[#030712] px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-bold flex items-center gap-2 transition-colors"
          >
            Comprar
            <ShoppingBag size={12} />
          </button>
        </div>
      </div>
    </article>
  );
}
